import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import Notification from './Notification';
import Spinner from './Spinner';
import ShareProduct from './ShareProduct';
import ImageGallery from './ImageGallery';
import './Products.css';
import useGlobalTheme from '../hooks/useGlobalTheme';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const ProductDetail = ({ cart, setCart }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [quantity, setQuantity] = useState(1);
  const [showShareModal, setShowShareModal] = useState(false);
  const { isDarkMode } = useGlobalTheme();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, show: true, type });
    setTimeout(() => {
      setNotification({ message: '', show: false });
    }, 3000);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (productDoc.exists()) {
          setProduct({ ...productDoc.data(), id: productDoc.id });
        } else {
          showNotification('Producto no encontrado.', 'error');
          navigate('/productos');
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        showNotification('Error al cargar el producto.', 'error');
        navigate('/productos');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId, navigate]);

  const addToCart = (product, quantity) => {
    const existingProduct = cart.find(item => item.id === product.id);
    const existingQuantity = existingProduct ? existingProduct.quantity : 0;
    const noStockCheck = product.excludeFromStock === true;

    if (!noStockCheck && product.stock < quantity + existingQuantity) {
      showNotification('No hay suficiente stock disponible.', 'error');
      return;
    }

    const updatedCart = existingProduct
      ? cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item)
      : [...cart, { ...product, quantity }];

    setCart(updatedCart);
    showNotification(`Añadido ${quantity} ${product.model} al carrito.`, 'success');
  };

  if (loading) return <Spinner />;

  if (!product) {
    return (
      <div className={`products-container ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="product-not-found">
          <h2>Producto no encontrado</h2>
          <button onClick={() => navigate('/productos')} className="back-button">
            Volver a Productos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`products-container ${isDarkMode ? 'dark' : 'light'}`}>
      {notification.show && (
        <Notification 
          message={notification.message} 
          show={notification.show} 
          type={notification.type} 
        />
      )}
      
      <div className="products-header">
        <button onClick={() => navigate('/productos')} className="back-button">
          ← Volver a Productos
        </button>
      </div>

      <div className="product-detail-container">
        <div className="product-detail-image-container">
          <ImageGallery 
            images={product.images || []} 
            alt={product.model} 
            imagePositions={product.imagePositions}
            className="product-detail-gallery"
          />
        </div>
        
        <div className="product-detail-info">
          <h2 className="product-detail-title">{product.model}</h2>
          <p className="product-detail-price">${formatNumber(product.price)}</p>
          <p className="product-detail-category">Categoría: {product.category}</p>
          {!product.excludeFromStock && (
            <p className="product-detail-stock">
              Stock disponible: {product.stock}
            </p>
          )}
          
          {product.description && (
            <div className="product-detail-description">
              <h3>Descripción:</h3>
              <p>{product.description}</p>
            </div>
          )}

          <div className="product-detail-actions">
            <div className="quantity-selector">
              <label htmlFor="quantity">Cantidad:</label>
              <input
                type="number"
                id="quantity"
                value={quantity}
                min={1}
                max={product.excludeFromStock ? 999 : product.stock}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="quantity-input"
              />
            </div>
            
            <button 
              className="add-to-cart-button" 
              onClick={() => addToCart(product, quantity)}
              disabled={!product.excludeFromStock && product.stock === 0}
            >
              {(product.excludeFromStock || product.stock > 0) ? 'Agregar al Carrito' : 'Sin Stock'}
            </button>
          </div>

          <div className="product-share-section">
            <h3>Compartir este producto:</h3>
            <div className="product-share-buttons">
              <button 
                className="product-share-button"
                onClick={() => setShowShareModal(true)}
              >
                📤 Compartir Producto
              </button>
            </div>
          </div>
        </div>
      </div>

      {showShareModal && (
        <ShareProduct 
          product={product} 
          onClose={() => setShowShareModal(false)} 
        />
      )}
    </div>
  );
};

export default ProductDetail; 